import { CNumber, useDriveStore } from "./DriveStore";
import React, { useState } from 'react';
import { useAnalytics } from '../third-party-integration/amplitude';
import { getDMSProxyFirewallUrl } from '../../util/getDMSProxyUrls';
import { useOktaAuth } from "@okta/okta-react";
import get from 'lodash/get';
import BaseCard from '../components/Cards/BaseCard';
import {
  IconOpenInNew
} from "cdk-radial";
import { MultipleDMSDialog } from './MultipleDMSDialog';
import { AMPLITUDE_EVENTS } from '../../constants';
import {driveLaunchFallback} from '../../util/driveLaunchFallback';
import { useTranslation } from 'react-i18next';
import userProfileStore from "../core/UserProfileStore";
import AdoptEaseDriveDialog from '../adopt-ease/AdoptEaseDriveDialog';
import { useAdoptEaseStore } from "../adopt-ease/useAdoptEaseStore";

interface Props {
  id: string;
  isEnabled: boolean;
  isFavorite?: boolean;
  description: string;
  name: string;
  iconColor: string;
  iconText: string;
  url: string;
  cdkuUrl?: string;
  onClick?: Function;
  onCdkUniversityClick?: Function;
  onFavoriteChange?: Function;
  isRestricted?: boolean;
  restrictedTooltipMsg?: string;
  isDefault?: boolean;
  onSetDefaultApp?: (appId: string, appName: string, isDefault: boolean) => void;
}

interface CardMenu {
  key: string;
  label: string;
  icon?: any;
  onClick: Function
}


function getDmsMappingWithName(cNumbers: CNumber[], dmsMapping: any) {
    if (!cNumbers || !dmsMapping || !dmsMapping.dmsBoxMappings) {
        return [];
    }
    return cNumbers?.map((cNumber: any) => {
        const dmsBoxAliasName =  dmsMapping?.dmsBoxMappings
            .find((eachBoxDetails: any) => (eachBoxDetails?.cnumber as string)
                .toLowerCase() === (cNumber).toLowerCase())
            ?.alias || '';
        return {
            'data-testid': cNumber,
            label: `${cNumber} ${dmsBoxAliasName && `- ${dmsBoxAliasName}`}`,
            value: cNumber,
            link: `https://${cNumber}-dms.drive.connectcdk.com/apps/autoTools/cds/osd/osd.php`,
            dmsBoxAliasName: dmsBoxAliasName?.toLowerCase()
        };
    });
}

export function canOpenDrive() {
  return navigator.userAgent.toLowerCase().indexOf('win') !== -1;
}

export default function DriveApplicationCard({
  id,
  isFavorite,
  description,
  name,
  iconText,
  iconColor,
  cdkuUrl,
  onCdkUniversityClick,
  onFavoriteChange,
  isRestricted,
  restrictedTooltipMsg,
  isDefault = false,
  onSetDefaultApp,
}: Props) {

  const { dmsMapping , cNumbers } = useDriveStore();
  const dmsDataWithName = getDmsMappingWithName(cNumbers, dmsMapping);
  const [shouldMultipleDMSDialogOpen, setDialogOpen] = React.useState<boolean>(false);
  const { trackEvent } = useAnalytics();
  const session = useOktaAuth();
  const authToken = get(session, 'authState.idToken.idToken', '');
  const locale = userProfileStore((state: any) => state.locale);
  const {t} = useTranslation();
  const filteredAdoptEaseWorkflows = useAdoptEaseStore((state: any) => state.filteredAdoptEaseWorkflows);

  const [showRecommendation, setShowRecommendation] = useState(false);
  const singleDmsBoxDetail = dmsDataWithName[0];
  const canOpen = canOpenDrive();

  
    const menu = (): CardMenu[] => {
        // Adaptiva OSD is only relevant for Windows users, since only they can open Drive
        if (!canOpen) {
            return [];
        }

        const options: CardMenu[] = [
            {
                label: "Adaptiva OSD", key: 'OPEN_DMS', icon: <IconOpenInNew />, onClick: () => {
                    if (cNumbers?.length > 1) {
                        setDialogOpen(true);
                    } else {
                        trackEvent(AMPLITUDE_EVENTS.OPEN_ADAPTIVA_OSD_SINGLE_DMS_USER, {
                            'C-Number': singleDmsBoxDetail?.value,
                            'Name': singleDmsBoxDetail?.label
                        });
                        window.open(`${singleDmsBoxDetail.link}`, '_blank');
                    }
                }
            }
        ];
        return options;
    };

  const menuOptions = menu();

  const handleOpenDrive = async () => {
        if(filteredAdoptEaseWorkflows && filteredAdoptEaseWorkflows.length > 0){
        setShowRecommendation(true);
        return;
    }  

    await onProceed();
  };
  
  const onProceed = async () => {


      trackEvent(AMPLITUDE_EVENTS.OPEN_APPLICATION, {
          Application: name,
      });

    let driveLaunchResponse;

    const multiDmsBoxUser =  cNumbers?.length > 1;
    if(multiDmsBoxUser){
        setDialogOpen(true);
        return;
    }

    const { url } = getDMSProxyFirewallUrl({
        cNumber: cNumbers[0],
        driveToken: authToken,
        lang :locale
    });

    driveLaunchResponse = await driveLaunchFallback(url);

    console.log(driveLaunchResponse,':drive launch');

    trackEvent(AMPLITUDE_EVENTS.OPEN_DRIVE, {
        'C-Number': dmsDataWithName[0]?.value, 'Name' : dmsDataWithName[0]?.label
    });
  
    };

    return (
        <>
            {cNumbers && cNumbers.length > 1 && (
                <MultipleDMSDialog
                    setDialogOpen={setDialogOpen}
                    dmsDataWithName={dmsDataWithName}
                    shouldDialogOpen={shouldMultipleDMSDialogOpen}
                />
            )}
            <BaseCard
                onClick={handleOpenDrive}
                name={name}
                iconColor={iconColor}
                learnMoreDescription={description}
                iconText={iconText}
                isEnabled={canOpen}
                warningMessage={canOpen ? '' : t('baseCard.driveWarningMsg')}
                isFavorite={isFavorite}
                onFavoriteChange={onFavoriteChange}
                isIconRequired={true}
                onCdkUniversityClick={onCdkUniversityClick}
                cdkuUrl={cdkuUrl}
                isRestricted={isRestricted}
                restrictedTooltipMsg={restrictedTooltipMsg}
                id={id}
                url={''}
                menu={menuOptions}
                showStartupOption={canOpen}
                isDefault={isDefault}
                onSetDefault={onSetDefaultApp}
            />

            { 
            showRecommendation && 
            <AdoptEaseDriveDialog
                isOpen={showRecommendation}
                onProceed={onProceed}
                onClose={() => setShowRecommendation(false)} />
            }

        </>
    );
}
